<template>
  <div>
    <!-- HERO 영역 (배경 이미지/틴트) -->
    <section class="hero">
      <div class="hero__overlay"></div>

      <!-- 헤더 (로고/메뉴) -->
      <header class="header">
        <AppLogo asLink to="/" size="md" :showText="true" />
      </header>
    </section>

    <!-- 메인 카드 -->
    <main class="main">
      <div class="container">
        <div class="card signup-card">
          <div class="signup-inner">
            <div class="signup-head">
              <div class="signup-badge">회원가입</div>
              <h1 class="signup-title">Sign up</h1>
            </div>
            <!-- STEP 1: 기본 정보 -->
            <form
              v-if="step === 1"
              class="signup-form"
              @submit.prevent="goTerms"
            >
              <UiInput
                placeholder="이름"
                v-model="form.name"
                autocomplete="name"
              />

              <!-- 이메일 -->
              <div class="field-wrap">
                <UiInput
                  placeholder="이메일"
                  type="email"
                  v-model="form.email"
                  autocomplete="email"
                />
                <p v-if="form.email && !emailValid" class="form-error">
                  이메일 형식이 올바르지 않습니다.
                </p>
              </div>

              <!-- 생년월일 -->
              <div class="field-wrap">
                <UiInput
                  placeholder="생년월일 (YYYYMMDD)"
                  v-model="form.birth"
                  maxlength="8"
                />
                <p v-if="form.birth && !birthValid" class="form-error">
                  생년월일 형식이 맞지 않습니다.
                </p>
              </div>

              <select class="ui-input" v-model="form.gender">
                <option value="">성별</option>
                <option value="M">남성</option>
                <option value="F">여성</option>
              </select>

              <!-- 비밀번호 -->
              <UiInput
                type="password"
                placeholder="비밀번호"
                v-model="form.password"
                autocomplete="new-password"
              />

              <!-- 비밀번호 재확인 + 에러 메시지 -->
              <div class="confirm-wrap">
                <UiInput
                  type="password"
                  placeholder="비밀번호 재확인"
                  v-model="form.passwordConfirm"
                  autocomplete="new-password"
                />
                <p v-if="passwordError" class="form-error">
                  {{ passwordError }}
                </p>
              </div>

              <UiButton
                type="submit"
                :disabled="!canSubmitStep1 || submitLoading"
                :loading="submitLoading"
              >
                다음
              </UiButton>
            </form>

            <!-- STEP 2: 약관 동의 -->
            <div v-else class="terms-step">
              <!-- 전체 동의 -->
              <label class="agree-all">
                <input
                  type="checkbox"
                  v-model="terms.agreeAll"
                  @change="toggleAgreeAll"
                />
                <span>전체 동의</span>
              </label>

              <div class="terms-list">
                <label class="term-item">
                  <input
                    type="checkbox"
                    v-model="terms.tos"
                    @change="syncAgreeAll"
                  />
                  <span>(필수) 이용약관 동의</span>
                  <button
                    type="button"
                    class="term-link"
                    @click="openSheet('tos')"
                  >
                    자세히
                  </button>
                </label>

                <label class="term-item">
                  <input
                    type="checkbox"
                    v-model="terms.privacy"
                    @change="syncAgreeAll"
                  />
                  <span>(필수) 개인정보처리방침 동의</span>
                  <button
                    type="button"
                    class="term-link"
                    @click="openSheet('privacy')"
                  >
                    자세히
                  </button>
                </label>

                <label class="term-item">
                  <input
                    type="checkbox"
                    v-model="terms.marketing"
                    @change="syncAgreeAll"
                  />
                  <span>(선택) 마케팅 정보 수신 동의</span>
                  <button
                    type="button"
                    class="term-link"
                    @click="openSheet('marketing')"
                  >
                    자세히
                  </button>
                </label>
              </div>

              <!-- 약관 보기 박스 (간단 placeholder) -->
              <div v-if="activeSheet" class="term-sheet">
                <div class="term-sheet__head">
                  <strong>{{ sheetTitle }}</strong>
                  <button
                    type="button"
                    class="sheet-close"
                    @click="activeSheet = ''"
                  >
                    닫기
                  </button>
                </div>
                <div class="term-sheet__body">
                  <p style="white-space: pre-line">{{ sheetBody }}</p>
                </div>
              </div>

              <div class="terms-actions">
                <UiButton
                  :disabled="!canSubmitStep2 || submitLoading"
                  :loading="submitLoading"
                  @click="finalSubmit"
                >
                  가입 완료
                </UiButton>
                <button class="link-btn" type="button" @click="step = 1">
                  이전으로
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 푸터 (요약 버전) -->
    <footer class="footer" style="margin-top: var(--space-64)">
      <div class="container footer-inner">
        <span class="hint">Copyright ⓒ 2025 Cardio Factory</span>
      </div>
    </footer>
  </div>
</template>

<script>
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AppLogo from "@/components/AppLogo.vue";
import { api } from "@/lib/api";

export default {
  name: "SignupView",
  components: { UiButton, UiInput, AppLogo },
  data() {
    return {
      step: 1, // ✅ 현재 단계 (1=기본정보, 2=약관)
      token: "",
      tokenValid: false,
      otpLoading: false,
      submitLoading: false,
      submittedOnce: false,
      form: {
        name: "",
        email: "",
        birth: "",
        gender: "",
        password: "",
        passwordConfirm: "",
      },
      terms: {
        // ✅ 약관 체크 상태
        agreeAll: false,
        tos: false,
        privacy: false,
        marketing: false,
      },
      activeSheet: "", // 'tos' | 'privacy' | 'marketing' | ''
      termsCache: {},
    };
  },

  computed: {
    emailValid() {
      const { email } = this.form;
      if (!email) return true;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    birthValid() {
      const { birth } = this.form;
      if (!birth) return true;
      const re = /^\d{8}$/; // ✅ YYYYMMDD
      return re.test(birth);
    },
    passwordTooShort() {
      const { password } = this.form;
      if (!password) return false;
      return password.length < 8;
    },
    passwordMatch() {
      const { password, passwordConfirm } = this.form;
      if (!password || !passwordConfirm) return true;
      return password === passwordConfirm;
    },
    passwordError() {
      const { password, passwordConfirm } = this.form;
      if (password && password.length < 8)
        return "비밀번호는 최소 8자 이상이어야 합니다.";
      if (password && passwordConfirm && !this.passwordMatch)
        return "비밀번호가 일치하지 않습니다.";
      return "";
    },

    // ✅ STEP1: 기본정보 완료 가능 여부
    canSubmitStep1() {
      const { name, email, birth, gender, password, passwordConfirm } =
        this.form;
      return (
        this.tokenValid &&
        name &&
        email &&
        birth &&
        gender &&
        password &&
        passwordConfirm &&
        !this.passwordTooShort &&
        this.passwordMatch &&
        this.emailValid &&
        this.birthValid
      );
    },

    // ✅ STEP2: 필수 약관 동의 여부
    canSubmitStep2() {
      return this.terms.tos && this.terms.privacy;
    },

    // 약관 보기 패널 타이틀/본문 (placeholder)
    sheetTitle() {
      switch (this.activeSheet) {
        case "tos":
          return "이용약관";
        case "privacy":
          return "개인정보처리방침";
        case "marketing":
          return "마케팅 정보 수신 동의";
        default:
          return "";
      }
    },
    sheetBody() {
      const map = {
        tos: "/terms/tos_2025-09-01.md",
        privacy: "/terms/privacy_2025-09-01.md",
        marketing: "/terms/marketing_2025-09-01.md",
      };
      const url = map[this.activeSheet];
      if (!url) return "";
      return this.termsCache[url] || "로딩 중…";
    },
  },
  created() {
    // /signup?token=xxxx 형태에서 토큰 추출
    this.token = (this.$route.query.token || "").toString();
    if (!this.token) {
      alert("유효하지 않은 접근입니다. 이메일 링크를 통해 접속해 주세요.");
      return;
    }
    this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        const res = await api("/v1/auth/invitations/verify", {
          method: "POST",
          body: JSON.stringify({ token: this.token }),
        });
        if (!res.ok) throw new Error("invalid");
        // 필요하면 user_id를 응답에서 읽어와 표시만 가능(서버 신뢰 X)
        this.tokenValid = true;
      } catch {
        alert("초대가 만료되었거나 유효하지 않습니다.");
        this.$router.replace("/");
      }
    },

    // STEP1 → STEP2
    goTerms() {
      if (!this.canSubmitStep1) return;
      this.step = 2;
      this.syncAgreeAll(); // 전체동의 상태 초기 동기화
    },

    // 전체 동의 토글
    toggleAgreeAll() {
      const v = this.terms.agreeAll;
      this.terms.tos = v;
      this.terms.privacy = v;
      this.terms.marketing = v;
    },
    // 개별 체크 변화 시 전체 동의 동기화
    syncAgreeAll() {
      this.terms.agreeAll =
        this.terms.tos && this.terms.privacy && this.terms.marketing;
    },

    async openSheet(which) {
      this.activeSheet = which;

      const map = {
        tos: "/terms/tos_2025-09-01.md",
        privacy: "/terms/privacy_2025-09-01.md",
        marketing: "/terms/marketing_2025-09-01.md",
      };
      const url = map[which];
      if (!url) return;

      // 캐시 초기화
      if (!this.termsCache) this.termsCache = {};

      // 이미 로딩되어 있으면 재사용
      if (this.termsCache[url]) return;

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("load failed");
        const text = await res.text();
        this.termsCache[url] = text; // ← Vue3는 이렇게 대입하면 됨
      } catch (e) {
        console.error(e);
        this.termsCache[url] =
          "약관을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.";
      }
    },

    // 최종 가입 (백엔드 호출 자리)
    async finalSubmit() {
      if (!this.canSubmitStep2) return;
      if (this.submitLoading || this.submittedOnce) return;
      this.submitLoading = true;
      this.submittedOnce = true;

      try {
        const res = await api("/v1/auth/invitations/accept", {
          method: "POST",
          body: JSON.stringify({
            token: this.token,
            name: this.form.name,
            password: this.form.password,
            email: this.form.email,
            birth_date: this.form.birth, // YYYYMMDD
            gender:
              this.form.gender === "M"
                ? "male"
                : this.form.gender === "F"
                ? "female"
                : "other",
            agree_to_terms: this.terms.tos,
            agree_to_privacy: this.terms.privacy,
            agree_to_marketing: this.terms.marketing,
          }),
        });

        if (!res.ok) throw new Error("가입 실패");
        alert("가입이 완료되었습니다. 환영합니다!");
        this.$router.replace("/");
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
        this.submitLoading = false;
        this.submittedOnce = false;
      }
    },
  },
};
</script>

<style scoped>
/* 카드 */
.signup-card {
  max-width: 758px;
  margin: 0 auto;
  padding: 48px 56px;
  background: #151718;
  border-radius: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
}

/* 내부 360px 컨테이너 → 제목/인풋/노트 시작선 일치 */
.signup-inner {
  max-width: 360px;
  margin: 0 auto;
}

/* 제목 */
.signup-head {
  margin-bottom: 24px;
  text-align: left;
}
.signup-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}
.signup-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.signup-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* ★ 핵심: UiInput(자식 컴포넌트)의 input에 깊게 스타일 주입 */
.signup-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important; /* 자식 scoped 규칙 덮어쓰기 */
  background: #151718;
  color: #fff;
  padding: 0 14px;
}
.signup-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}
.signup-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

/* select는 현재 컴포넌트 안에 있으므로 일반 선택자로 충분 */
.signup-form select.ui-input {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff;
  background: #151718;
  color: #fff;
  padding: 0 10px;
}

/* 버튼 폭 */
.signup-form .ui-btn {
  width: 100%;
}

/* 안내문구 */
.signup-note {
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  text-align: left;
}

/* 반응형 */
@media (max-width: 640px) {
  .signup-card {
    padding: 28px 20px;
    border-radius: 20px;
  }
  .signup-title {
    font-size: 32px;
    letter-spacing: -1.2px;
  }
}

.confirm-wrap {
  display: grid;
  gap: 6px; /* 인풋과 에러 문구 간격 */
}

.form-error {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #ff4d4f; /* 빨강 */
}

.footer .container {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding-inline: 16px;
  display: flex;
  justify-content: flex-start;
  gap: 16px;
}

.field-wrap {
  display: grid;
  gap: 6px;
}

.confirm-wrap {
  display: grid;
  gap: 6px;
}

.hero {
  height: 220px; /* 기존 hero 높이가 크면 줄이기 */
  position: relative;
}

.form-error {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #ff4d4f; /* 빨강 */
}
/* 약관 단계 */
.terms-step {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* 전체 동의 */
.agree-all {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 700;
}

/* 약관 목록 */
.terms-list {
  display: grid;
  gap: 12px;
}
.term-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.term-link {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: var(--brand, #78ff47);
  cursor: pointer;
  font-size: 14px;
}

/* 약관 보기 박스 */
.term-sheet {
  background: #0f1011;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: grid;
  grid-template-rows: auto 1fr;
  max-height: 240px;
  overflow: hidden;
}
.term-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 700;
}
.term-sheet__body {
  padding: 12px;
  overflow: auto;
  line-height: 1.6;
  font-size: 14px;
}
.sheet-close {
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
}

/* 액션 */
.terms-actions {
  display: grid;
  gap: 10px;
}
.link-btn {
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  text-decoration: underline;
  justify-self: start;
}
</style>
